<template>
  <b-modal v-model="show" centered hide-footer no-close-on-backdrop :title="title" @hidden="reset">
    <div v-if="loadingOptions" class="tw-py-6 tw-text-center">
      <b-spinner small />
    </div>

    <template v-else-if="step === 'form' && options">
      <p v-if="initial" class="tw-text-sm tw-mb-3">
        {{
          $t(
            "gateways.crowded_manual_payout_initial_intro",
            "Your Crowded account has {amount} available. Would you like to send a payout now? Otherwise it will be paid out automatically with the next daily payout.",
            { amount: formatMoney(options.available_cents) }
          )
        }}
      </p>

      <b-alert v-if="noFunds" show variant="warning" class="tw-text-sm">
        {{
          $t(
            "gateways.crowded_manual_payout_no_funds",
            "There are no funds available to pay out yet. Released donations will be available here once they settle in your Crowded account."
          )
        }}
      </b-alert>

      <div class="form-group tw-mb-1">
        <label class="form-group__label form-group__label--required" for="crowded-payout-amount">
          {{ $t("gateways.crowded_manual_payout_amount", "Amount") }}
        </label>
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text">$</span>
          </div>
          <input
            id="crowded-payout-amount"
            v-model="amount"
            type="number"
            inputmode="decimal"
            min="1"
            step="0.01"
            class="form-control"
            :disabled="noFunds"
            :class="{ 'is-invalid': amountError }"
          />
          <div class="input-group-append">
            <button
              type="button"
              class="btn btn-link"
              :disabled="maxCents < options.min_cents"
              @click="setMax"
            >
              {{ $t("gateways.crowded_manual_payout_max", "Max amount") }}
            </button>
          </div>
        </div>
        <div v-if="amountError" class="invalid-feedback d-block">{{ amountError }}</div>
      </div>
      <p v-if="limitsText" class="tw-text-xs tw-text-[#6f7a8a] tw-mb-3">{{ limitsText }}</p>

      <div class="crowded-payout-parties tw-mb-3">
        <div class="crowded-payout-parties__row">
          <div class="tw-text-xs tw-text-[#6f7a8a]">
            {{ $t("gateways.crowded_manual_payout_from", "From") }}
          </div>
          <div>{{ $t("gateways.crowded_manual_payout_from_balance", "Crowded balance") }}</div>
          <div class="tw-text-xs tw-text-[#6f7a8a]">
            {{
              $t("gateways.crowded_manual_payout_available", "Available balance: {amount}", {
                amount: formatMoney(options.available_cents),
              })
            }}
          </div>
        </div>
        <div class="crowded-payout-parties__row crowded-payout-parties__row--to">
          <div class="tw-text-xs tw-text-[#6f7a8a]">
            {{ $t("gateways.crowded_manual_payout_to", "To") }}
          </div>
          <div>{{ options.recipient.name }}</div>
          <div class="tw-text-xs tw-text-[#6f7a8a]">
            {{ $t("gateways.crowded_payout_routing", "Routing") }}
            {{ options.recipient.routing_number }} •
            {{ $t("gateways.crowded_manual_payout_account", "Account") }}
            ••{{ options.recipient.account_last4 }}
          </div>
        </div>
      </div>

      <div class="tw-text-xs tw-font-bold tw-uppercase tw-text-[#6f7a8a] tw-mb-1">
        {{ $t("gateways.crowded_manual_payout_speed", "Delivery speed") }}
      </div>
      <div class="crowded-payout-speeds tw-mb-3">
        <label class="crowded-payout-speed">
          <input v-model="sameDay" type="radio" :value="false" />
          <span class="crowded-payout-speed__body">
            <span>{{ $t("gateways.crowded_manual_payout_standard", "Standard ACH") }}</span>
            <small class="tw-text-[#6f7a8a]">
              {{ $t("gateways.crowded_manual_payout_standard_time", "1-3 business days") }}
            </small>
          </span>
          <span class="crowded-payout-speed__fee">
            {{ $t("gateways.crowded_manual_payout_free", "Free") }}
          </span>
        </label>
        <label
          class="crowded-payout-speed"
          :class="{ 'crowded-payout-speed--disabled': noFunds || !options.same_day_supported }"
        >
          <input
            v-model="sameDay"
            type="radio"
            :value="true"
            :disabled="noFunds || !options.same_day_supported"
          />
          <span class="crowded-payout-speed__body">
            <span>
              {{ $t("gateways.crowded_manual_payout_same_day", "Same-Day ACH") }}
              <b-badge pill variant="primary" class="tw-ml-1">
                {{ $t("gateways.crowded_manual_payout_fast", "Fast") }}
              </b-badge>
            </span>
            <small class="tw-text-[#6f7a8a]">
              {{
                noFunds || options.same_day_supported
                  ? $t(
                      "gateways.crowded_manual_payout_same_day_time",
                      "Submit before 3:00 PM ET for same-day processing."
                    )
                  : $t(
                      "gateways.crowded_manual_payout_same_day_unavailable",
                      "Not available for this account."
                    )
              }}
            </small>
          </span>
          <span class="crowded-payout-speed__fee">
            {{ $t("gateways.crowded_manual_payout_same_day_fee", "1% of transfer amount") }}
            <br />
            {{ $t("gateways.crowded_manual_payout_same_day_fee_range", "(min $5 and max $25)") }}
          </span>
        </label>
      </div>

      <div class="form-group">
        <label class="form-group__label" for="crowded-payout-reference">
          {{ $t("gateways.crowded_manual_payout_reference", "Reference (optional)") }}
        </label>
        <input
          id="crowded-payout-reference"
          v-model="reference"
          type="text"
          class="form-control"
          maxlength="80"
        />
        <small class="form-text text-muted">
          {{
            $t(
              "gateways.crowded_manual_payout_reference_help",
              "Shown with the deposit by banks that support it. Letters, numbers and punctuation only."
            )
          }}
        </small>
      </div>

      <p class="tw-text-xs tw-text-[#6f7a8a]">
        {{
          $t(
            "gateways.crowded_manual_payout_total_note",
            "Total debited from your balance = amount + delivery fee."
          )
        }}
      </p>

      <error v-if="errors.length" :errors="errors" />

      <div class="tw-flex tw-justify-end tw-gap-2">
        <button
          v-if="initial"
          type="button"
          class="bttn bttn--sm bttn--grey"
          :disabled="busy"
          @click="show = false"
        >
          {{ $t("gateways.crowded_manual_payout_not_now", "Not now") }}
        </button>
        <button
          type="button"
          class="bttn bttn--sm bttn--orange"
          :disabled="busy || !canReview"
          @click="review"
        >
          <b-spinner v-if="busy" small class="tw-mr-1" />
          {{ $t("gateways.crowded_manual_payout_review", "Review") }}
        </button>
      </div>
    </template>

    <template v-else-if="step === 'review' && quote">
      <div class="alert alert-warning tw-text-sm">
        {{
          sameDay
            ? $t(
                "gateways.crowded_manual_payout_same_day_warning",
                "Same-day payouts submitted after 3:00 PM ET, or on a weekend or holiday, are processed the next business day. A returned ACH transaction may incur an additional fee."
              )
            : $t(
                "gateways.crowded_manual_payout_standard_warning",
                "Payouts submitted after 3:00 PM ET, or on a weekend or holiday, start processing the next business day and usually arrive within 1-3 business days. A returned ACH transaction may incur an additional fee."
              )
        }}
      </div>

      <dl class="crowded-payout-review">
        <dt>{{ $t("gateways.crowded_manual_payout_to", "To") }}</dt>
        <dd>{{ options.recipient.name }}</dd>
        <dt>{{ $t("gateways.crowded_manual_payout_amount", "Amount") }}</dt>
        <dd>{{ formatMoney(quote.amount_cents) }}</dd>
        <dt>{{ $t("gateways.crowded_manual_payout_fee", "Fee") }}</dt>
        <dd>
          {{
            quote.fee_cents > 0
              ? formatMoney(quote.fee_cents)
              : $t("gateways.crowded_manual_payout_free", "Free")
          }}
        </dd>
        <dt>{{ $t("gateways.crowded_manual_payout_speed", "Delivery speed") }}</dt>
        <dd>
          {{
            sameDay
              ? $t("gateways.crowded_manual_payout_same_day", "Same-Day ACH")
              : $t("gateways.crowded_manual_payout_standard", "Standard ACH")
          }}
        </dd>
        <dt>{{ $t("gateways.crowded_manual_payout_date", "Date") }}</dt>
        <dd>{{ today }}</dd>
        <dt>{{ $t("gateways.crowded_manual_payout_routing", "Routing #") }}</dt>
        <dd>{{ options.recipient.routing_number }}</dd>
        <dt>{{ $t("gateways.crowded_manual_payout_account_number", "Account #") }}</dt>
        <dd>•••••{{ options.recipient.account_last4 }}</dd>
        <template v-if="reference.trim()">
          <dt>{{ $t("gateways.crowded_manual_payout_reference_label", "Reference") }}</dt>
          <dd>{{ reference.trim() }}</dd>
        </template>
        <dt class="crowded-payout-review__total">
          {{ $t("gateways.crowded_manual_payout_total", "Total") }}
        </dt>
        <dd class="crowded-payout-review__total">{{ formatMoney(quote.total_debit_cents) }}</dd>
      </dl>

      <error v-if="errors.length" :errors="errors" />

      <div class="tw-flex tw-justify-between tw-gap-2">
        <button
          type="button"
          class="bttn bttn--sm bttn--grey"
          :disabled="busy"
          @click="step = 'form'"
        >
          {{ $t("common.back", "Back") }}
        </button>
        <button
          type="button"
          class="bttn bttn--sm bttn--orange"
          :disabled="busy || !quote.sufficient"
          @click="confirm"
        >
          <b-spinner v-if="busy" small class="tw-mr-1" />
          {{ $t("gateways.crowded_manual_payout_confirm", "Confirm") }}
        </button>
      </div>
    </template>

    <template v-else-if="step === 'done' && result">
      <div class="tw-text-center tw-py-2">
        <h5 class="tw-mb-2">
          {{ $t("gateways.crowded_manual_payout_created", "Payout created") }}
        </h5>
        <p class="tw-mb-1">
          {{
            $t(
              "gateways.crowded_manual_payout_created_text",
              "{amount} is on its way to {name} (••{last4}).",
              {
                amount: formatMoney(result.amount_cents),
                name: options.recipient.name,
                last4: options.recipient.account_last4,
              }
            )
          }}
        </p>
        <p class="tw-text-sm tw-text-[#6f7a8a]">
          {{
            result.same_day
              ? $t(
                  "gateways.crowded_manual_payout_created_same_day",
                  "Same-day ACH: submitted before 3:00 PM ET, it arrives today."
                )
              : $t(
                  "gateways.crowded_manual_payout_created_standard",
                  "Standard ACH: usually arrives within 1-3 business days."
                )
          }}
        </p>
        <p class="tw-text-xs tw-text-[#6f7a8a]">
          {{
            $t(
              "gateways.crowded_manual_payout_cancel_note",
              "To cancel a pending payout, visit your Crowded dashboard at"
            )
          }}
          <a :href="crowdedAppUrl" target="_blank" rel="noopener noreferrer">{{ crowdedAppUrl }}</a>
        </p>
        <button type="button" class="bttn bttn--sm bttn--orange" @click="show = false">
          {{ $t("common.close", "Close") }}
        </button>
      </div>
    </template>

    <error v-else-if="errors.length" :errors="errors" />
  </b-modal>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls.js";
import { CROWDED_APP_URL } from "@/constants/crowded.js";
import error from "@/components/errors.vue";

function uuidV4() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Org-initiated Crowded ACH payout: amount + delivery speed → review → confirm.
// open({ initial: true }) is the "pay out your balance now?" prompt shown right
// after a payout account is saved; it resolves false (and stays closed) when
// there is nothing to pay out.
export default {
  name: "CrowdedManualPayout",

  components: { error },

  props: {
    // The base "crowded" gateway (organization_merchant_info id) to pay out from.
    gatewayId: { type: [Number, String], required: true },
  },

  data() {
    return {
      show: false,
      initial: false,
      step: "form",
      loadingOptions: false,
      busy: false,
      errors: [],
      options: null,
      amount: "",
      sameDay: false,
      reference: "",
      quote: null,
      idempotencyKey: "",
      result: null,
      crowdedAppUrl: CROWDED_APP_URL,
    };
  },

  computed: {
    orgId() {
      return localStorage.getItem("orgId");
    },

    requestConfig() {
      return { params: { gateway_id: this.gatewayId } };
    },

    title() {
      if (this.step === "review") {
        return this.$t("gateways.crowded_manual_payout_review_title", "Review payout");
      }
      return this.$t("gateways.crowded_manual_payout_title", "Create a payout");
    },

    amountCents() {
      const n = Number(this.amount);
      return Number.isFinite(n) ? Math.round(n * 100) : 0;
    },

    maxCents() {
      if (!this.options) return 0;
      return this.sameDay ? this.options.max_same_day_cents : this.options.max_standard_cents;
    },

    amountError() {
      if (this.amount === "" || !this.options) return "";
      if (this.amountCents < this.options.min_cents) {
        return this.$t(
          "gateways.crowded_manual_payout_min_error",
          "The minimum payout is {amount}.",
          {
            amount: this.formatMoney(this.options.min_cents),
          }
        );
      }
      if (this.amountCents > this.maxCents) {
        return this.$t(
          "gateways.crowded_manual_payout_max_error",
          "The maximum for this payout is {amount}.",
          { amount: this.formatMoney(this.maxCents) }
        );
      }
      return "";
    },

    // Nothing to send yet (e.g. donations not released). The backend only probes same-day eligibility
    // when there is something to send, so don't call same-day "not available" in that case.
    noFunds() {
      return !!this.options && this.options.max_standard_cents < this.options.min_cents;
    },

    canReview() {
      return !this.noFunds && this.amount !== "" && !this.amountError;
    },

    limitsText() {
      const o = this.options;
      if (!o || (o.daily_remaining_cents < 0 && o.monthly_remaining_cents < 0)) return "";
      if (o.daily_remaining_cents >= 0 && o.monthly_remaining_cents >= 0) {
        return this.$t(
          "gateways.crowded_manual_payout_limits",
          "{daily} daily remaining ({monthly} monthly remaining)",
          {
            daily: this.formatMoney(o.daily_remaining_cents),
            monthly: this.formatMoney(o.monthly_remaining_cents),
          }
        );
      }
      return o.daily_remaining_cents >= 0
        ? this.$t("gateways.crowded_manual_payout_limit_daily", "{amount} daily remaining", {
            amount: this.formatMoney(o.daily_remaining_cents),
          })
        : this.$t("gateways.crowded_manual_payout_limit_monthly", "{amount} monthly remaining", {
            amount: this.formatMoney(o.monthly_remaining_cents),
          });
    },

    today() {
      return new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },
  },

  watch: {
    // A same-day max is lower than standard (it leaves room for the fee).
    sameDay() {
      if (this.amount !== "" && this.amountCents > this.maxCents && this.maxCents > 0) {
        this.setMax();
      }
    },
  },

  methods: {
    open({ initial = false } = {}) {
      this.reset();
      this.initial = initial;
      this.loadingOptions = true;
      if (!initial) {
        this.show = true;
      }

      return axios
        .get(urls.crowdedPayoutOptions.replace(":oid", this.orgId), this.requestConfig)
        .then(({ data }) => {
          this.options = data;
          if (
            initial &&
            (!data.manual_payout_allowed || data.max_standard_cents < data.min_cents)
          ) {
            return false;
          }
          this.show = true;
          return true;
        })
        .catch((e) => {
          if (initial) return false;
          this.showError(e);
          return false;
        })
        .finally(() => {
          this.loadingOptions = false;
        });
    },

    reset() {
      this.step = "form";
      this.busy = false;
      this.errors = [];
      this.options = null;
      this.amount = "";
      this.sameDay = false;
      this.reference = "";
      this.quote = null;
      this.idempotencyKey = "";
      this.result = null;
    },

    setMax() {
      this.amount = (this.maxCents / 100).toFixed(2);
    },

    formatMoney(cents) {
      const n = Number(cents || 0) / 100;
      return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
    },

    review() {
      this.errors = [];
      this.busy = true;
      axios
        .post(
          urls.crowdedPayoutQuote.replace(":oid", this.orgId),
          {
            amount_cents: this.amountCents,
            same_day: this.sameDay,
          },
          this.requestConfig
        )
        .then(({ data }) => {
          if (!data.sufficient) {
            this.errors.push(
              this.$t(
                "gateways.crowded_manual_payout_insufficient",
                "Your balance does not cover this amount plus the delivery fee."
              )
            );
            return;
          }
          this.quote = data;
          // One key per review screen: a double click or a retry after a
          // network error sends the payout at most once.
          this.idempotencyKey = uuidV4();
          this.step = "review";
        })
        .catch(this.showError)
        .finally(() => {
          this.busy = false;
        });
    },

    confirm() {
      this.errors = [];
      this.busy = true;
      axios
        .post(
          urls.crowdedPayouts.replace(":oid", this.orgId),
          {
            amount_cents: this.quote.amount_cents,
            same_day: this.sameDay,
            reference: this.reference.trim(),
            idempotency_key: this.idempotencyKey,
          },
          this.requestConfig
        )
        .then(({ data }) => {
          this.result = data;
          this.step = "done";
          this.$emit("created", data);
        })
        .catch(this.showError)
        .finally(() => {
          this.busy = false;
        });
    },

    showError(e) {
      const d = e?.response?.data;
      if (d?.error) {
        this.errors.push(d.error);
      } else if (Array.isArray(d?.errors)) {
        this.errors = d.errors.map((err) => err.detail || err.title).filter(Boolean);
      } else {
        this.errors.push(this.$t("template.error_default", "Something went wrong"));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.crowded-payout-parties {
  border: 1px solid #e6eaed;
  border-radius: 8px;
  overflow: hidden;

  &__row {
    padding: 10px 14px;

    &--to {
      border-top: 1px solid #e6eaed;
      background-color: #f8f9fa;
    }
  }
}

.crowded-payout-speeds {
  border: 1px solid #e6eaed;
  border-radius: 8px;
}

.crowded-payout-speed {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 10px 14px;
  cursor: pointer;

  & + & {
    border-top: 1px solid #e6eaed;
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__fee {
    text-align: right;
    font-size: 14px;
    white-space: nowrap;
  }
}

.crowded-payout-review {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 16px;

  dt {
    font-weight: normal;
    color: #6f7a8a;
  }

  dd {
    margin: 0;
    text-align: right;
  }

  &__total {
    padding-top: 8px;
    border-top: 1px solid #e6eaed;
    font-family: $vito-bold;
    color: $fd;
  }
}
</style>
